import React, { useState } from "react";
import { Flex, Input, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import { PATH_PICK_BUYER_INFO_INPUT_STYLE_PROPS } from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketBuyerInfoSection";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useGetNaverPartnerStatus from "@/features/pathpick/features/basket/hooks/query/useGetNaverPartnerStatus";
import useConnectNaverPartner from "@/features/pathpick/features/basket/hooks/mutation/useConnectNaverPartner";
import useDisconnectNaverPartner from "@/features/pathpick/features/basket/hooks/mutation/useDisconnectNaverPartner";

// API 연동용 판매자ID를 연결/해제하는 필드.
// 판매자 기본 정보 설정 모달의 "스마트스토어 API 정보" 섹션 안에 들어간다(카드 헤더는 섹션 셸이 그린다).
//
// 이 값만은 모달의 저장 버튼과 무관하다 — 연결/해제 전용 API가 따로 있어 버튼을 누르는 즉시 반영되고,
// 연결 여부는 폼이 아니라 연결 상태 조회(connected)가 단독으로 판정한다.
function PathPickBasketNaverPartnerField() {
  const { t } = useI18nHelper();
  const {
    naverPartnerStatus,
    isError: isStatusError,
    isFetching: isStatusFetching,
    refetch: refetchStatus,
  } = useGetNaverPartnerStatus();
  const { mutate: connectNaverPartner, isPending: isConnecting } =
    useConnectNaverPartner();
  const { mutate: disconnectNaverPartner, isPending: isDisconnecting } =
    useDisconnectNaverPartner();

  const isConnected = Boolean(naverPartnerStatus?.connected);
  // 다시 확인하다 실패해도 앞서 받아둔 상태가 있으면 그대로 보여준다 —
  // 연동 여부를 아예 모를 때만 조회 실패로 전환한다.
  const isStatusUnavailable = isStatusError && naverPartnerStatus === undefined;
  const serverAccountId = naverPartnerStatus?.accountId ?? "";
  // null = 아직 사용자가 손대지 않음 → 서버 값을 그대로 보여준다.
  // 연동이 끊겼지만 판매자ID가 남아 있으면 다시 타이핑하지 않고 [연동]만 누르면 되게 한다.
  const [editedAccountId, setEditedAccountId] = useState<string | null>(null);
  const [isDisconnectConfirmOpen, setIsDisconnectConfirmOpen] =
    useState<boolean>(false);

  // 연동된 계정은 서버 값을 그대로 보여주고, 미연동일 때만 입력을 받는다.
  const value = isConnected
    ? serverAccountId
    : editedAccountId ?? serverAccountId;

  // 연동을 시도한 적이 없으면(저장된 판매자ID도 없음) 실패로 단정할 수 없어 아무 문구도 띄우지 않는다.
  // 판정은 서버 값으로 한다 — 입력창에 타이핑하는 중에 실패 문구가 뜨면 안 된다.
  // 상태 조회 자체가 실패했다면 연동 여부를 모르는 것이지 미연동이 아니다.
  const statusMessageKey = isStatusUnavailable
    ? "path_pick:basket.buyer_info.api_info.status_unavailable"
    : isConnected
    ? "path_pick:basket.buyer_info.api_info.connected"
    : serverAccountId
    ? "path_pick:basket.buyer_info.api_info.not_connected"
    : null;

  const handleConnect = () => connectNaverPartner({ accountId: value.trim() });

  const handleDisconnect = () =>
    disconnectNaverPartner(undefined, {
      onSuccess: () => {
        // 해제 뒤 남는 판매자ID는 서버 응답이 정한다 — 편집분을 버려 서버 값으로 되돌린다.
        setEditedAccountId(null);
        setIsDisconnectConfirmOpen(false);
      },
    });

  return (
    <Flex flexDirection={"column"} gap={"6px"}>
      <Flex gap={"8px"} alignItems={"center"}>
        <Input
          value={value}
          onChange={(event) => setEditedAccountId(event.target.value)}
          placeholder={t(
            "path_pick:basket.buyer_info.api_info.seller_id_placeholder"
          )}
          isReadOnly={isConnected}
          flexGrow={1}
          {...PATH_PICK_BUYER_INFO_INPUT_STYLE_PROPS}
          {...(isConnected && {
            background: "#EFEFED",
            cursor: "default",
          })}
        />
        <Flex flexShrink={0}>
          {isConnected ? (
            <AppButton
              variant={"white"}
              size={"small"}
              // 연동 여부를 모르는 상태에서는 연결/해제 어느 쪽도 누르게 두지 않는다 —
              // 이미 연동된 계정에 다시 연동 요청을 보내는 사고가 난다.
              isDisabled={isStatusUnavailable}
              isLoading={isDisconnecting}
              onClick={() => setIsDisconnectConfirmOpen(true)}
            >
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:basket.buyer_info.api_info.disconnect")}
              </Text>
            </AppButton>
          ) : (
            <AppButton
              variant={"primary"}
              size={"small"}
              isDisabled={isStatusUnavailable || !value.trim()}
              isLoading={isConnecting}
              onClick={handleConnect}
            >
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:basket.buyer_info.api_info.connect")}
              </Text>
            </AppButton>
          )}
        </Flex>
      </Flex>

      {/* 연동 결과 — 입력 줄 바로 아래 한 줄로 알린다.
          조회 실패는 사용자가 고칠 수 있는 게 아니라 다시 확인하는 것뿐이라 버튼을 함께 준다. */}
      {statusMessageKey && (
        <Flex gap={"8px"} alignItems={"center"}>
          <Text
            textStyle={"body-sm-medium"}
            color={isConnected && !isStatusUnavailable ? "#4C994F" : "#CC1D1D"}
          >
            {t(statusMessageKey)}
          </Text>
          {isStatusUnavailable && (
            <AppButton
              variant={"white"}
              size={"small"}
              isLoading={isStatusFetching}
              onClick={() => refetchStatus()}
            >
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:basket.buyer_info.api_info.recheck")}
              </Text>
            </AppButton>
          )}
        </Flex>
      )}

      {isDisconnectConfirmOpen && (
        <CustomApprovalModal
          isOpen
          onClose={() => setIsDisconnectConfirmOpen(false)}
          // 기본 폭(480px)이 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
          minWidth={"min(480px, 92dvw)"}
          isCentered={true}
        >
          <CustomApprovalModal.Header
            onClose={() => setIsDisconnectConfirmOpen(false)}
            title={t(
              "path_pick:basket.buyer_info.api_info.disconnect_confirm.title"
            )}
          />
          <CustomApprovalModal.Body>
            <Text textStyle={"body-md-regular"} color={"#1D1D1B"}>
              {t(
                "path_pick:basket.buyer_info.api_info.disconnect_confirm.body"
              )}
            </Text>
          </CustomApprovalModal.Body>
          <CustomApprovalModal.Footer
            cancelName={t("common:cancel")}
            confirmName={t(
              "path_pick:basket.buyer_info.api_info.disconnect_confirm.confirm"
            )}
            onCancel={() => setIsDisconnectConfirmOpen(false)}
            onConfirm={handleDisconnect}
            isLoading={isDisconnecting}
          />
        </CustomApprovalModal>
      )}
    </Flex>
  );
}

export default PathPickBasketNaverPartnerField;
